import {
  Box,
  Typography,
  IconButton,
  Avatar,
  Tooltip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  ArrowBack,
  MoreVert,
  PersonAdd,
  ExitToApp,
  Settings,
  PushPin,
  PushPinOutlined,
} from '@mui/icons-material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ConversationDto, ConversationType } from '@api/types/chat'
import { useAuthStore } from '@store/authStore'

interface ChatHeaderProps {
  conversation: ConversationDto | null
  onBack?: () => void
  onSettings?: () => void
  onAddParticipants?: () => void
  onPin?: (pinned: boolean) => void
  onLeave?: () => void
  isPinned?: boolean
}

export function ChatHeader({
  conversation,
  onBack,
  onSettings,
  onAddParticipants,
  onPin,
  onLeave,
  isPinned = false,
}: ChatHeaderProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { user } = useAuthStore()
  const navigate = useNavigate()

  // Determine if header should be clickable to navigate to group/adventure
  const getNavigationUrl = () => {
    if (conversation?.groupId) {
      return `/groups/${conversation.groupId}`
    }
    if (conversation?.adventureId) {
      return `/adventures/${conversation.adventureId}`
    }
    return null
  }

  const handleHeaderClick = () => {
    const url = getNavigationUrl()
    if (url) {
      navigate(url)
    }
  }

  const isClickable = !!getNavigationUrl()

  if (!conversation) {
    return (
      <Box
        sx={{
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Typography variant="subtitle1" color="text.secondary">
          Select a conversation
        </Typography>
      </Box>
    )
  }

  // Filter out current user from participants
  const otherParticipants = conversation.participants.filter(
    (p) => p.userId !== user?.userId
  )

  const displayName =
    conversation.name ||
    otherParticipants.map((p) => p.displayName).join(', ') ||
    'Chat'

  const participantCount = conversation.participants.length
  const onlineCount = otherParticipants.filter((p) => p.isOnline).length

  const getSubtitle = () => {
    if (conversation.type === ConversationType.Direct) {
      const participant = otherParticipants[0]
      return participant?.isOnline ? 'Online' : 'Offline'
    }
    return `${participantCount} members${onlineCount > 0 ? `, ${onlineCount} online` : ''}`
  }

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchor(null)
  }

  return (
    <Box
      sx={{
        p: 1.5,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'background.paper',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {/* Back button for mobile */}
      {isMobile && onBack && (
        <IconButton onClick={onBack} edge="start">
          <ArrowBack />
        </IconButton>
      )}

      {/* Avatar(s) */}
      <Box
        onClick={isClickable ? handleHeaderClick : undefined}
        sx={isClickable ? { cursor: 'pointer' } : undefined}
      >
        {conversation.type === ConversationType.Direct ? (
          <Avatar
            sx={{ width: 40, height: 40 }}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
        ) : (
          <Avatar
            src={
              (conversation.type === ConversationType.Group ? conversation.groupAvatar : conversation.adventureAvatar) || undefined
            }
            sx={{ width: 40, height: 40 }}
          >
            {displayName.charAt(0).toUpperCase()}
          </Avatar>
        )}
      </Box>

      {/* Title and subtitle */}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          ...(isClickable && {
            cursor: 'pointer',
            '&:hover': { opacity: 0.8 },
          }),
        }}
        onClick={isClickable ? handleHeaderClick : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0, overflow: 'hidden' }}>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {displayName}
          </Typography>
          {conversation.type === ConversationType.Adventure && (
            <Chip
              label="Adventure"
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                fontWeight: 600,
                flexShrink: 0,
                bgcolor: alpha(theme.palette.secondary.main, 0.15),
                color: 'secondary.main',
              }}
            />
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {getSubtitle()}
        </Typography>
      </Box>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Pin button */}
        {onPin && (
          <Tooltip title={isPinned ? 'Unpin' : 'Pin'}>
            <IconButton size="small" onClick={() => onPin(!isPinned)}>
              {isPinned ? <PushPin fontSize="small" /> : <PushPinOutlined fontSize="small" />}
            </IconButton>
          </Tooltip>
        )}

        {/* More menu */}
        <IconButton size="small" onClick={handleMenuOpen}>
          <MoreVert fontSize="small" />
        </IconButton>
      </Box>

      {/* Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {/* Add participants (groups only) */}
        {conversation.type !== ConversationType.Direct && onAddParticipants && (
          <MenuItem
            onClick={() => {
              handleMenuClose()
              onAddParticipants()
            }}
          >
            <ListItemIcon>
              <PersonAdd fontSize="small" />
            </ListItemIcon>
            <ListItemText>Add Participants</ListItemText>
          </MenuItem>
        )}

        {/* Settings */}
        {onSettings && (
          <MenuItem
            onClick={() => {
              handleMenuClose()
              onSettings()
            }}
          >
            <ListItemIcon>
              <Settings fontSize="small" />
            </ListItemIcon>
            <ListItemText>Settings</ListItemText>
          </MenuItem>
        )}

        {/* Leave conversation (groups/adventures only) */}
        {conversation.type !== ConversationType.Direct && onLeave && (
          <MenuItem
            onClick={() => {
              handleMenuClose()
              onLeave()
            }}
          >
            <ListItemIcon>
              <ExitToApp fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText sx={{ color: 'error.main' }}>Leave Conversation</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  )
}
